import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { SeoService } from '../../services/seo.service';

const SITE_NAME = 'TheNearByShop';

/**
 * Applies each route's `title` (suffixed with the site name), plus `data.description`
 * and a canonical URL. Product/shop pages still set richer tags themselves after
 * their data loads, which simply overrides these defaults.
 */
@Injectable({ providedIn: 'root' })
export class AppTitleStrategy extends TitleStrategy {
  private title = inject(Title);
  private meta = inject(Meta);
  private seo = inject(SeoService);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    // Canonical = path without query string, so filtered/sorted variants consolidate.
    const path = snapshot.url.split('?')[0].split('#')[0] || '/';
    this.seo.setCanonicalPath(path);

    const routeTitle = this.buildTitle(snapshot);
    // Dynamic pages (product, category, shop) have no static title and set their own
    // tags once data loads - this runs after their ngOnInit, so don't overwrite them.
    if (!routeTitle) return;

    const fullTitle = `${routeTitle} | ${SITE_NAME}`;
    this.title.setTitle(fullTitle);

    const description = this.deepestData(snapshot.root, 'description');
    if (description) {
      this.meta.updateTag({ name: 'description', content: description });
      this.meta.updateTag({ property: 'og:description', content: description });
      this.meta.updateTag({ name: 'twitter:description', content: description });
    }
    this.meta.updateTag({ property: 'og:title', content: fullTitle });
    this.meta.updateTag({ name: 'twitter:title', content: fullTitle });
  }

  private deepestData(route: ActivatedRouteSnapshot, key: string): string | undefined {
    let value: string | undefined;
    let current: ActivatedRouteSnapshot | null = route;
    while (current) {
      value = current.data?.[key] ?? value;
      current = current.firstChild;
    }
    return value;
  }
}
